import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Button from '../molecules/Button';
import { AppState } from '../../store/root.reducer';
import AppData from '../3d/app.data';
import './ButtonContainer.scss';
import { axiosInstance } from '../../utils/api';
import { toast } from 'react-toastify';
import { useInstructionsContext } from '../../contexts/InstructionsContext';
import { ContactModal } from '../modals/contact.modal';
import { FeedbackModal } from '../modals/feedback.modal';
import { VolumeMuteFill, VolumeUpFill } from 'react-bootstrap-icons';
import { useGalleryChromeContext } from '../../contexts/GalleryChromeContext';

const appData = new AppData();

const ADMIN_ROLES = ['GALLERY', 'ADMIN', 'SUPERADMIN'];
const BASE_API = process.env.REACT_APP_BASE_API;

interface ButtonContainerProps {
  isMomGallery?: boolean;
  showFeedback?: boolean;
  onFeedbackModalChange?: (open: boolean) => void;
}

export const ButtonContainer = ({ 
  isMomGallery = false, 
  showFeedback = false, 
  onFeedbackModalChange,
}: ButtonContainerProps) => {
  const navigate = useNavigate();
  const { user } = useSelector((state: AppState) => state.auth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openSpaceGallery, setOpenSpaceGallery] = useState(false);
  const [galleryName, setGalleryName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showContactMenu, setShowContactMenu] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showVisualSettings, setShowVisualSettings] = useState(false);
  const {
    flashingEffectsEnabled,
    hasPlayableGalleryBackgroundMusic,
    setFlashingEffectsEnabled,
    setVisitorBackgroundMusicMuted,
    visitorBackgroundMusicMuted,
  } = useGalleryChromeContext();

  const { onMinify } = useInstructionsContext();

  const setKeyAvailability = (available: boolean) => {
    if (appData.main3DManager) {
      appData.main3DManager.setKeyAvailability(available);
    }
  };

  const handleAddGallery = () => {
    setKeyAvailability(false);
    setOpenSpaceGallery(false);
    setIsModalOpen(true);
  };

  const handleCreateGallery = async () => {
    if (galleryName.trim() && !isCreating) {
      setIsCreating(true);
      try {
        const { data } = await axiosInstance.post(`${BASE_API}/galleries`, {
          galleryName: galleryName.trim(),
          spaceType: openSpaceGallery ? "open-space" : "procedural"
        })

        if (data.status) {
          toast.success("Gallery created successfully!", {
            position: "bottom-left",
          });
          setGalleryName('');
          setOpenSpaceGallery(false);
          setIsModalOpen(false);
          setKeyAvailability(true);
          navigate(`/gallery/${data.gallery._id}?t=personal`);
        } else {
          toast.error(data.msg || "Failed to create gallery", {
            position: "bottom-left",
          });
        }
      } catch (error) {
        toast.error(error.response?.data?.msg || "Error creating gallery.\nPlease try again.", {
          position: "bottom-left",
          style: { 
            width: '400px',
            whiteSpace: 'pre-line'
          },
          className: 'wider-toast'
        });
      } finally {
        setIsCreating(false);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setGalleryName('');
    setOpenSpaceGallery(false);
    setIsCreating(false);
    setKeyAvailability(true);
  };

  const canCreateGallery = user && user.roles && user.roles.some((role: string) => ["SELLER"].includes(role));

  const [renderQuality, setRenderQuality] = useState<"LQ" | "MQ" | "HQ">(() => {
    if (typeof window === "undefined") return "MQ";
    const stored = localStorage.getItem("renderQuality");
    if (stored === "LQ" || stored === "MQ" || stored === "HQ") {
      return stored as "LQ" | "MQ" | "HQ";
    }
    localStorage.setItem("renderQuality", "MQ");
    return "MQ";
  });

  const onQualityChanged = (quality: "LQ" | "MQ" | "HQ") => {
    localStorage.setItem('renderQuality', quality);
    window.location.reload();
  }

  const handleFeedbackMenuClick = () => {
    setShowVisualSettings(false);
    setShowContactMenu((open) => !open);
  };

  const handleVisualSettingsClick = () => {
    setShowContactMenu(false);
    setShowVisualSettings((open) => !open);
  };

  const handleVisitorBackgroundMusicMute = () => {
    const nextMuted = !visitorBackgroundMusicMuted;
    setVisitorBackgroundMusicMuted(nextMuted);
    if (nextMuted) {
      appData.main3DManager?.soundService?.mute();
    } else {
      void appData.main3DManager?.soundService?.unmute();
    }
  };

  const handleFlashingEffectsChanged = () => {
    const nextEnabled = !flashingEffectsEnabled;
    setFlashingEffectsEnabled(nextEnabled);
    appData.flashingEffectsEnabled = nextEnabled;
    appData.main3DManager?.rendererService?.addToRenderQueue?.();
  };

  const handleFeedbackItemClick = () => {
    setShowContactMenu(false);
    setShowFeedbackModal(true);
    setKeyAvailability(false);
    onFeedbackModalChange?.(true);
  };

  const handleFeedbackModalHide = () => {
    setShowFeedbackModal(false);
    setKeyAvailability(true);
    onFeedbackModalChange?.(false);
  };

  const handleFeedbackShared = (status: boolean) => {
    if (status) {
      toast.success("Shared your feedback", {
        position: "bottom-left",
      });
    } else {
      toast.error("Failed to share feedback", {
        position: "bottom-left",
      });
    }
    handleFeedbackModalHide();
  };

  const handleContactUsItemClick = () => {
    setShowContactMenu(false);
    setShowContactModal(true);
    setKeyAvailability(false);
  };

  const handleContactModalHide = () => {
    setShowContactModal(false);
    setKeyAvailability(true);
  };

  const handleContactSent = (status: boolean, msg?: string) => {
    if (status) {
      toast.success(msg || "Message sent successfully!", {
        position: "bottom-left",
      });
    } else {
      toast.error(msg || "Failed to send message. Please try again.", {
        position: "bottom-left",
      });
    }
  };

  return (
    <div className="button-container">
      {showFeedback && (
        <div className="menu-button-group">
          <button
            className="icon-button"
            aria-label="Contact us"
            data-tooltip="Contact us"
            data-tooltip-position="left"
            onClick={handleFeedbackMenuClick}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </button>
          {showContactMenu && (
            <div className="settings-menu" id="contactMenu">
              <button className="menu-option" onClick={handleFeedbackItemClick}>
                Feedback
              </button>
              <button className="menu-option" onClick={handleContactUsItemClick}>
                Contact us
              </button>
            </div>
          )}
        </div>
      )}
      {hasPlayableGalleryBackgroundMusic && (
        <button
          type="button"
          className="icon-button"
          aria-label={visitorBackgroundMusicMuted ? "Unmute background music" : "Mute background music"}
          aria-pressed={visitorBackgroundMusicMuted}
          data-tooltip={visitorBackgroundMusicMuted ? "Unmute background music" : "Mute background music"}
          data-tooltip-position="left"
          onClick={handleVisitorBackgroundMusicMute}
        >
          {visitorBackgroundMusicMuted
            ? <VolumeMuteFill aria-hidden="true" focusable="false" />
            : <VolumeUpFill aria-hidden="true" focusable="false" />}
        </button>
      )}
      <div className="menu-button-group">
        <button
          className="icon-button"
          aria-label="Visual settings"
          aria-expanded={showVisualSettings}
          aria-controls="settingsMenu"
          data-tooltip="Visual settings"
          data-tooltip-position="left"
          onClick={handleVisualSettingsClick}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33 1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82 1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </button>
        {showVisualSettings && (
          <div className="settings-menu" id="settingsMenu">
            <button className="menu-option" onClick={() => onQualityChanged("MQ")}>High definition</button>
            <button className="menu-option" onClick={() => onQualityChanged("LQ")}>Performance</button>
            <button
              type="button"
              role="switch"
              aria-checked={flashingEffectsEnabled}
              aria-label="Flashing effects"
              className="menu-option menu-option--switch"
              onClick={handleFlashingEffectsChanged}
            >
              <span>Flashing effects</span>
              <span className="menu-option__state" aria-hidden="true">
                {flashingEffectsEnabled ? "On" : "Off"}
              </span>
            </button>
          </div>
        )}
      </div>
      {canCreateGallery && isMomGallery && (
        <button 
          type="button"
          aria-label="Add gallery"
          className="icon-button add-gallery-btn" 
          data-tooltip="Add gallery"
          data-tooltip-position="left"
          onClick={handleAddGallery}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" strokeLinecap="round"></path>
          </svg>
        </button>
      )}
      <button 
        type="button"
        aria-label="Instructions"
        data-tooltip-position="left"
        className="icon-button" data-tooltip="Instructions" onClick={onMinify}>
        <svg viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2">
          <circle cx="12" cy="12" r="8" fill="#d1d5db"/> 
          <path d="M12 16v-4m0-4h.01" stroke="#374151" strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
      </button>
      
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Create New Gallery</h3>
            <div className="form-group">
              <label htmlFor="galleryName">Gallery Name:</label>
              <input
                id="galleryName"
                type="text"
                value={galleryName}
                onChange={(e) => setGalleryName(e.target.value)}
                placeholder="Enter gallery name"
                className="form-control"
                autoFocus
              />
            </div>
            <div className="form-group checkbox-group">
              <input 
                id="openSpaceGallery"
                type="checkbox"
                checked={openSpaceGallery}
                onChange={e => setOpenSpaceGallery(e.target.checked)}
              />
              <label htmlFor='openSpaceGallery'>Open Space Gallery</label>
            </div>
            <div className="modal-buttons">
              <Button onClick={handleModalClose} small>
                Cancel
              </Button>
              <Button onClick={handleCreateGallery} small disabled={!galleryName.trim() || isCreating}>
                {isCreating ? 'Creating...' : 'Create Gallery'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ContactModal
        show={showContactModal}
        onHide={handleContactModalHide}
        onSent={handleContactSent}
      />

      <FeedbackModal
        show={showFeedbackModal}
        onHide={handleFeedbackModalHide}
        onShared={handleFeedbackShared}
      />
    </div>
  );
};
